import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { ensureContactsTenantIdentity } from '@modules/contacts/services/contacts-tenant-identity';
import { IGNORE_SPACE_CONTEXT, ITEM_OWNER_ID, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import { Component, signal, inject, Injector, Input, OnInit, type SimpleChanges } from '@angular/core';
import { AlertService } from '@modules/alert/services/alert.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Tab } from '@modules/common/types/tab';
import { isNil } from '@modules/common/utils/base';
import { ContactListStateService } from '@modules/contacts/services/contact-list-state.service';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupsService } from '@modules/contacts/services/groups.service';
import { ContactInfo } from '@modules/contacts/types/contact-info';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { ContactsListState } from '@modules/contacts/types/contacts-list-state';
import { Group, type GroupFormGroupConfig } from '@modules/contacts/types/group';
import { type DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { contactInfoTypes } from '@modules/form-controls/types/contactInfoTypes';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { catchError, combineLatest, type Observable, of, skip, Subject } from 'rxjs';
import { filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.contactsFFListState]?: ContactsListState;
  }
}

@Component({
  selector: 'app-group-form',
  templateUrl: './group-form.component.html',
  styleUrls: ['./group-form.component.less'],
  standalone: false,
  providers: [
    WorkspaceIdentityService,
    ...ContactListStateService.providers({
      stateKey: StateKey.contactsFFListState,
    }),
    provideDetailFooterSlot(),
  ],
})
export class GroupFormComponent
  extends FullFormBaseComponent<StitchType.GROUP, Group, GroupFormGroupConfig>
  implements OnInit
{
  protected readonly sharingTenantId = signal<string | null>(null);
  private readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });
  private readonly itemIdentityService = inject(WorkspaceIdentityService);
  private openingWorkspaceConfig: StitchRequestConfig | undefined;
  protected readonly workspaceIdentity = toSignal(
    this.stitchItemChanged.pipe(
      skip(1),
      filter(() => !this.stitchItem?.id),
      take(1),
      switchMap(() =>
        this.spaceContextStore.type() === SpaceType.SHARED_SPACE
          ? this.itemIdentityService.current().pipe(take(1))
          : of(undefined),
      ),
      tap((identity) => {
        if (identity && !this.stitchItem?.id) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.contactsTenantScope?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      }),
      catchError(() => of(undefined)),
    ),
    { initialValue: undefined },
  );

  public changesKey = 'group';
  tabs: Tab[] = [
    { title: 'Contacts', value: 'group' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'groups';
  public parentSuggestions: AutocompleteFactory<Group>;
  public contactsFilters: ContactsFilters;

  protected dragDataType = DragDataTypes.group;
  protected override childItemDragType = DragDataTypes.contact;
  protected override childItemContainerIdField = 'groupId';
  public readonly contactInfoTypes = contactInfoTypes;

  @Input() group = new Group();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(ContactsAppStateService, { appLabel: APP_CRUMBS.contacts });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Contacts app › Groups › this group. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.groups,
      fallbackView: TREE_CRUMBS.groups,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  private readonly contactListStateService = inject(ContactListStateService, { self: true });
  private createAudienceItem: Group | undefined;
  private awaitingCreateAudience = false;
  private readonly cancelPendingCreate = new Subject<void>();
  private readonly settledTenant = toSignal(
    this.contactsTenantScope?.selectedTeamId?.() ?? of(this.contactsTenantScope?.tenantId?.()),
    { initialValue: undefined },
  );
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;
  private readonly appStateService = inject(BaseAppStateService);

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.group,
      childItemType: DragDataTypes.contact,
      childService: this.contactsService,
      containerIdField: 'groupId',
      appStateService: this.appStateService,
      spaceId: this.spaceContextStore.contextId(),
    };
  }

  constructor(
    private groupsService: GroupsService,
    private contactsService: ContactsService,
    injector: Injector,
  ) {
    super(injector, groupsService);
    this.sharingTenantId.set(this.contactsTenantScope?.effectiveTenantId() ?? null);
    ensureContactsTenantIdentity(this.contactsTenantScope, injector, this.destroyRef);
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = (...args) => {
        const context = this.stitchItem?.accountId
          ? contactsOwnerContext([this.stitchItem])
          : (this.requestConfig().requestContext ??
            this.requestContext ??
            contactsOwnerContext(undefined, this.settledTenant()));
        return context
          ? this.groupsService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] }, () => context)(...args)
          : of([]);
      };
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.contactListStateService.value(), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([listState, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.contactsFilters = new ContactsFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
        this.contactsFilters.applyListState(listState);
      });
  }

  dndDrop(dragData: DragData): void {
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  handleAddItem(type: 'emails' | 'phones', index?: number): void {
    const control = this.form.controls[type];
    const newItem = ContactInfo.createItem(type);

    control.insert((!isNil(index) ? index : control.length) + 1, newItem);
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.group && this.createAudienceItem && this.stitchItem !== this.createAudienceItem) {
      if (this.awaitingCreateAudience) {
        this.cancelPendingCreate.next();
        this.saveInProgress = false;
      }
      // A retained create audience belongs to that draft, never the next input in this reused host.
      this.requestContext = this.stitchItem?.accountId
        ? contactsOwnerContext([this.stitchItem])
        : changes.requestContext
          ? this.requestContext
          : undefined;
      this.createAudienceItem = undefined;
    }
    if (changes.group && !this.stitchItem?.id) {
      this.openingWorkspaceConfig = this.spaceContextStore.type() === SpaceType.PERSONAL
        ? { requestContext: contactsOwnerContext(undefined, null) }
        : undefined;
      const identity = this.workspaceIdentity();
      this.sharingTenantId.set(
        this.stitchItem?.accountId ?? this.contactsTenantScope?.effectiveTenantId() ?? identity?.memberTenantId ?? null,
      );
    }
    super.ngOnChanges(changes);
  }

  public override handleCancel(): void {
    if (this.awaitingCreateAudience) {
      this.cancelPendingCreate.next();
      this.saveInProgress = false;
    }
    super.handleCancel();
  }

  get stitchItem(): Group {
    return this.group;
  }

  protected get draftOwnerLocked(): boolean {
    const owner = this.stitchItem?.accountId;
    const identity = this.workspaceIdentity();
    return !!owner && owner !== identity?.spaceTenantId && owner !== identity?.memberTenantId;
  }

  protected setSharingTenantId(tenantId: string | null): void {
    const identity = this.workspaceIdentity();
    if (this.stitchItem.id || this.saveInProgress || this.draftOwnerLocked || this.form.controls.uploads.value.length)
      return;
    if (!identity || (tenantId !== identity.spaceTenantId && tenantId !== identity.memberTenantId)) return;
    if (tenantId === this.sharingTenantId()) return;
    this.sharingTenantId.set(tenantId);
    this.stitchItem.accountId = tenantId;
    this.stitchItem.parentId = null;
    this.form.controls.accountId.setValue(tenantId, { emitEvent: false });
    this.form.controls.parentId.setValue(null);
    this.requestContext = contactsOwnerContext(undefined, tenantId);
    this.originalFormValue = { ...this.originalFormValue, accountId: tenantId, parentId: null };
  }

  protected override tenantOwnerId(): string | undefined {
    if (!this.stitchItem?.id && this.workspaceIdentity() && !this.draftOwnerLocked) {
      return this.sharingTenantId() ?? this.stitchItem?.accountId;
    }
    return this.stitchItem?.accountId;
  }

  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) return { tenantItems: [this.stitchItem] };
    if (this.openingWorkspaceConfig && !(
      this.requestContext?.get(ITEM_OWNER_ID) || this.requestContext?.get(OWNING_SPACE_ID) ||
      this.requestContext?.get(IGNORE_SPACE_CONTEXT)
    )) return this.openingWorkspaceConfig;
    const tenantId = this.workspaceIdentity() ? this.sharingTenantId() : null;
    return tenantId ? { requestContext: contactsOwnerContext(undefined, tenantId) } : super.requestConfig();
  }

  protected override pendingSave(): Observable<void> | undefined {
    return super.pendingSave();
  }

  protected shouldRefreshList(prev, current) {
    return Group.shouldRefreshList(prev, current);
  }

  /** A shell draft has no owner until Contacts identity answers; keep that audience for retries. */
  protected override performCreate(stitchItem: Group): Observable<Group> {
    this.createAudienceItem = this.stitchItem;
    const suppliedContext = this.requestConfig().requestContext;
    const suppliedOwner = suppliedContext?.get(ITEM_OWNER_ID) ?? suppliedContext?.get(OWNING_SPACE_ID);
    const carriedContext = this.stitchItem.accountId
      ? contactsOwnerContext([this.stitchItem])
      : suppliedOwner
        ? contactsOwnerContext(undefined, suppliedOwner)
        : suppliedContext?.get(IGNORE_SPACE_CONTEXT)
          ? suppliedContext
          : undefined;
    this.awaitingCreateAudience = true;
    const audience = carriedContext
      ? of(carriedContext)
      : (this.contactsTenantScope?.selectedTeamId() ?? of(null)).pipe(
          map((owner) => contactsOwnerContext(undefined, owner)),
        );
    return audience.pipe(
      filter((context) => context !== undefined),
      take(1),
      takeUntil(this.cancelPendingCreate),
      takeUntil(this.alive),
      tap((context) => {
        this.awaitingCreateAudience = false;
        this.requestContext = context;
        const owner = context.get(ITEM_OWNER_ID);
        if (owner) {
          this.stitchItem.accountId = owner;
          stitchItem.accountId = owner;
          this.form.controls.accountId.setValue(owner, { emitEvent: false });
        }
      }),
      switchMap(() => super.performCreate(stitchItem)),
      finalize(() => {
        this.awaitingCreateAudience = false;
      }),
    );
  }

  protected fromFormGroup(): Group {
    return Group.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.group.asFormGroup({ groupsService: this.groupsService, maxAttachmentSize: this.maxUploadSize() });
  }
}
